<template>
  <v-card class="card--shadow">
    <v-card-title class="purple accent-4">
      <div class="subtitle-1 white--text">Supervision Visit Form</div>
    </v-card-title>
    <v-card class="card--shadow mb-2" v-if="foundPatient">
      <v-card-text>
        <div class="d-flex">
          <p class="subtitle-1 patient-info" v-if="foundPatient.name">
            Name - {{ foundPatient.name }}
          </p>
          <p class="ml-3 subtitle-1 patient-info" v-if="foundPatient.reg_unique_code">
            Code - {{ foundPatient.reg_unique_code }}
          </p>
        </div>
      </v-card-text>
    </v-card>
    <v-card-text class="pt-4">
      <form-require-noti />
      <v-row>
        <v-col lg="3">
          <label>Patient's Township <require-sign /></label>
                <v-select
                :items="townships"
                v-model="supervisionData.patient_township_id"
                item-text="name"
                item-value="id"
                dense
                outlined
                ></v-select>
        </v-col>
        <v-col lg="3">
          <label>Code <require-sign /></label>
          <small class="text-muted">&nbsp; (eg. 99 or 2)</small>
          <v-text-field
            v-model="supervisionData.mdrtb_code"
            :error-messages="handleFieldErrors($v.supervisionData.mdrtb_code)"
            hide-details
            type="text"
            outlined
            dense
          ></v-text-field>
        </v-col>
        <v-col lg="3">
          <label>Year <require-sign /></label>
          <small class="text-muted">&nbsp; (eg. 24)</small>
          <v-text-field
            v-model="supervisionData.mdrtb_year"
            :error-messages="handleFieldErrors($v.supervisionData.mdrtb_year)"
            hide-details
            type="text"
            outlined
            dense
          ></v-text-field>
        </v-col>
        <v-col lg="3">
          <label>Mono-Poly<require-sign /></label>
        <v-select
          v-model="supervisionData.mdrtb_monopoly"
          :items="[
            { id: 1, text: 'No' },
            { id: 2, text: 'Yes' },
            { id: 3, text: 'SG' },
            { id: 4, text: 'MGY' },
          ]"
          :error="$v.supervisionData.mdrtb_monopoly.$error"
          item-text="text"
          item-value="id"
          dense
          outlined
        ></v-select>
        </v-col>
      </v-row>
      <v-row align="center">
        <v-col cols="12" lg="9">
          <v-card outlined class="outside-township-card pa-3" elevation="0">
            <div class="d-flex align-center flex-wrap">
              <div class="outside-township-label mr-3">
                <v-icon small class="mr-1">mdi-map-marker-outline</v-icon>
                Outside Township
              </div>
              <v-radio-group
                v-model="supervisionData.outside_township"
                class="mt-0 flex-grow-0 outside-township-radio"
                hide-details
                dense
                row
              >
                <v-radio label="No" value="1" color="teal accent-4"></v-radio>
                <v-radio label="Yes" value="2" color="teal accent-4"></v-radio>
              </v-radio-group>
              <v-expand-transition>
                <v-select
                  v-if="showOutsideTownshipSelect"
                  :items="outside_townships"
                  v-model="supervisionData.outside_township_id"
                  item-text="name"
                  item-value="id"
                  dense
                  outlined
                  clearable
                  placeholder="Choose township"
                  class="ml-lg-4 mt-2 mt-lg-0"
                  :error-messages="
                    handleFieldErrors($v.supervisionData.outside_township_id)
                  "
                  style="min-width: 200px;"
                  @focus="fetchOutsideTownshipsIfNeeded({ clearOutsideId: false })"
                >
                  <template v-slot:label>
                    <span>
                      Select township
                      <require-sign v-if="showOutsideTownshipSelect" />
                    </span>
                  </template>
                </v-select>
              </v-expand-transition>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" lg="3" class="d-flex align-center">
          <v-btn @click.prevent="searchData" :loading="loading" class="form-btn">
            <v-icon left>mdi-magnify</v-icon>Search
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col lg="3">
          <label>Volunteer's Township <require-sign /></label>
                <v-select
                :items="townships"
                v-model="supervisionData.volunteer_township_id"
                item-text="name"
                item-value="id"
                @change="handleSupportingTownship"
                dense
                outlined
                ></v-select>
        </v-col>
        <v-col lg="3">
          <label>Volunteer <require-sign /></label>
          <v-select
            :items="volunteers"
            v-model="supervisionData.volunteer_id"
            @input="changeVolunteer"
            :error-messages="handleFieldErrors($v.supervisionData.volunteer_id)"
            item-value="id"
            dense
            outlined
          >
            <template v-slot:selection="{ item }">
              <span>{{ item.name }} / {{ item.code }}</span>
            </template>
            <template v-slot:item="{ item }">
              <span>{{ item.name }} / {{ item.code }}</span>
            </template>
          </v-select>
        </v-col>
        <v-col lg="3">
          <label>DOT Type <require-sign /></label>
          <v-select
            :items="[
              { value: 1, text: `Through DOT application` },
              { value: 2, text: `By DOT Card` },
            ]"
            v-model="supervisionData.dots_type"
            :error-messages="handleFieldErrors($v.supervisionData.dots_type)"
            item-text="text"
            item-value="value"
            dense
            outlined
          ></v-select>
        </v-col>
        <v-col lg="3">
          <label>Supervision Type <require-sign /></label>
          <v-select
            :items="[
              { value: 1, text: `In person` },
              { value: 2, text: `By Phone` },
            ]"
            v-model="supervisionData.supervision_type"
            :error-messages="
              handleFieldErrors($v.supervisionData.supervision_type)
            "
            item-text="text"
            item-value="value"
            dense
            outlined
          ></v-select>
        </v-col>
       
      </v-row>
      <v-row>
        <v-col lg="6">
          <label>Date <require-sign /></label>
          <!-- <date-picker
            @clearDate="supervisionData.date = null"
            :validationMsg="handleFieldErrors($v.supervisionData.date)"
            :date="supervisionData.date"
            v-model="supervisionData.date"
            :min="minDate"
            :max="maxDate"
          /> -->
          <date-picker
              @clearDate="supervisionData.date = null"
              :date="supervisionData.date"
              v-model="supervisionData.date"
            />
        </v-col>
      </v-row>
      <v-divider />
      <v-simple-table dense>
        <tbody>
          <tr
            v-for="result in supervisionData.radio_value_list"
            :key="result.id"
          >
            <td width="70%">{{ showText(result.id) }}</td>
            <td>
              <v-radio-group
                class="my-2"
                v-model="result[Object.keys(result)[1]]"
                hide-details
                dense
                row
              >
                <v-radio label="Yes" value="2"></v-radio>
                <v-radio label="No" value="1"></v-radio>
              </v-radio-group>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
      <v-divider />
      <v-row>
        <v-col class="ml-3 mt-3" lg="8">
          <label>Remark</label>
          <v-text-field
            v-model.trim="supervisionData.remark"
            type="text"
            filled
            dense
          ></v-text-field>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions class="px-4 pb-4">
      <v-spacer></v-spacer>
      <v-btn @click.prevent="emitData" class="form-btn">
        <v-icon left>mdi-chevron-right-circle-outline</v-icon>Submit
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import { ValidationMixin } from "../../mixins/ValidationMixin";
import { limitDateMixin } from "../../mixins/limitDateMixin";
import { mapState, mapActions } from "vuex";
import FormRequireNoti from "@/components/notification/FormRequireNoti.vue";
import RequireSign from "@/components/notification/RequireSign.vue";
import DatePicker from "@/components/common/DatePicker.vue";

export default {
  components: { FormRequireNoti, RequireSign, DatePicker },
  mixins: [ValidationMixin, limitDateMixin],
  props: {
    supervisionData: {
      type: Object,
      default: () => {
        return {
          mdrtb_monopoly:null,
          mdrtb_code: null,
          mdrtb_year: null,
          dots_type: null,
          supervision_type: null,
          township_id:null,
          patient_township_id:null,
          volunteer_township_id:null,
          outside_township: "1",
          outside_township_id: null,
          remark: null,
          radio_value_list: [
            { id: 1, value_1: "1" },
            { id: 2, value_2: "1" },
            { id: 3, value_3: "1" },
            { id: 4, value_4: "1" },
            { id: 5, value_5: "1" },
            { id: 6, value_6: "1" },
            { id: 7, value_7: "1" },
            { id: 8, value_8: "1" },
            { id: 9, value_9: "1" },
            { id: 10, value_9: "1" },
            { id: 11, value_10: "1" },
          ],
        };
      },
    },
  },
  data: () => ({
    menuDate: null,
    foundPatient: null,
    radioText: [
      { id: 1, text: "Performing DOTS regulary" },
      { id: 2, text: "Complete Recording" },
      { id: 3, text: "Wearing N95" },
      { id: 4, text: "Health Education" },
      { id: 5, text: "Using Relevant IEC materials" },
      { id: 6, text: "Has Relevant TB knowledge to share" },
      { id: 7, text: "Side Effect monitoring" },
      { id: 8, text: "Systematic Contact Investigation" },
      { id: 9, text: "Good Communication and Relationship" },
      { id: 10, text: "Good Linkage" },
      { id: 11, text: "Counseling" },
    ],
  }),
  validations: {
    supervisionData: {
      mdrtb_code: { required },
      mdrtb_year: { required },
      mdrtb_monopoly:{required},
      patient_township_id:{required},
      volunteer_township_id:{required},
      volunteer_id: { required },
      dots_type: { required },
      supervision_type: { required },
      date: { required },
      outside_township_id: {
        required() {
          return this.showOutsideTownshipSelect
            ? required(this.supervisionData.outside_township_id)
            : true;
        },
      },
    },
  },
  watch: {
    "supervisionData.outside_township": {
      immediate: true,
      handler(val) {
        if (val != null && val !== "") {
          this.$set(
            this.supervisionData,
            "outside_township",
            String(val).trim()
          );
        }
        const yes =
          this.supervisionData.outside_township === "2" ||
          Number(val) === 2;
        const no =
          this.supervisionData.outside_township === "1" ||
          Number(val) === 1;
        if (yes) {
          this.fetchOutsideTownshipsIfNeeded({ clearOutsideId: false });
        } else if (no) {
          this.supervisionData.outside_township_id = null;
        }
      },
    },
    "supervisionData.patient_township_id"(val, oldVal) {
      if (oldVal === undefined || oldVal === null || oldVal === "") return;
      if (this.showOutsideTownshipSelect && val) {
        this.fetchOutsideTownshipsIfNeeded({ clearOutsideId: true });
      }
    },
    "supervisionData.id"(val) {
      if (val != null && val !== "") {
        this.normalizeOutsideTownshipFromPayload();
        this.$nextTick(() =>
          this.fetchOutsideTownshipsIfNeeded({ clearOutsideId: false })
        );
      }
    },
    outside_townships(val) {
      if (!val || !val.length) return;
      if (!this.showOutsideTownshipSelect) return;
      this.$nextTick(() =>
        this.applyOutsideTownshipSelectionAfterListLoads()
      );
    },
  },
  computed: {
    ...mapState("Loading", ["loading"]),
    ...mapState("Volunteer", ["volunteers"]),
    ...mapState("Patient", ["patients"]),
    ...mapState("InfoData", ["townships", "outside_townships"]),
    ...mapState("Admin", ["admin"]),
    showOutsideTownshipSelect() {
      const v = this.supervisionData.outside_township;
      return v === "2" || v === 2 || Number(v) === 2;
    },
  },
  methods: {
    ...mapActions("InfoData", ["getAllTownships", "getOutsideTownships"]),
    ...mapActions("Patient", ["getByCode"]),
    async searchData() {
      this.$v.supervisionData.mdrtb_code.$touch();
      this.$v.supervisionData.mdrtb_year.$touch();
      this.$v.supervisionData.patient_township_id.$touch();
      if (
        this.$v.supervisionData.mdrtb_code.$error ||
        this.$v.supervisionData.mdrtb_year.$error ||
        this.$v.supervisionData.patient_township_id.$error
      ) return;

      this.foundPatient = null;
      const monopoly = Number(this.supervisionData.mdrtb_monopoly);
      const outsideTownship = Number(this.supervisionData.outside_township);
      const result = await this.getByCode({
        mdrtb_reg_no: this.supervisionData.mdrtb_code,
        mdrtb_reg_year: this.supervisionData.mdrtb_year,
        mdrtb_monopoly: monopoly,
        township_id: this.supervisionData.patient_township_id,
        outside_township: outsideTownship,
        ...(outsideTownship === 2 &&
          this.supervisionData.outside_township_id != null && {
            outside_township_id: this.supervisionData.outside_township_id,
          }),
      });
      if (result) {
        this.foundPatient = result;
      } else {
        this.$toast.error("Patient not found");
      }
    },
    normalizeOutsideTownshipFromPayload() {
      this.syncOutsideTownshipFromPatient();
      const d = this.supervisionData;
      let idRaw = d.outside_township_id;
      if (
        (idRaw == null || idRaw === "") &&
        d.outside_tsp_name != null &&
        d.outside_tsp_name !== ""
      ) {
        idRaw = d.outside_tsp_name;
      }
      const id = this.coerceOutsideTownshipRowId(idRaw);
      if (id != null) this.$set(this.supervisionData, "outside_township_id", id);
    },
    /**
     * When patient.outside_tsp is yes, use patient.outside_tsp_name (row id) for the select.
     */
    syncOutsideTownshipFromPatient() {
      const s = this.supervisionData;
      const p = s && s.patient;
      if (!p || typeof p !== "object") return;

      const osp = p.outside_tsp;
      const patientOutsideYes =
        osp === 2 ||
        osp === "2" ||
        Number(osp) === 2 ||
        osp === true;
      if (!patientOutsideYes) return;

      this.$set(this.supervisionData, "outside_township", "2");

      const rawId =
        p.outside_tsp_name ??
        p.outside_township_id ??
        (p.outside_township && typeof p.outside_township === "object"
          ? p.outside_township.id
          : null);
      const id = this.coerceOutsideTownshipRowId(rawId);
      if (id != null) this.$set(this.supervisionData, "outside_township_id", id);

      const ptId =
        p.patient_township_id ??
        p.township_id ??
        (p.township && typeof p.township === "object" ? p.township.id : null);
      if (
        ptId != null &&
        ptId !== "" &&
        (s.patient_township_id == null || s.patient_township_id === "")
      ) {
        const n = Number(ptId);
        if (!Number.isNaN(n)) s.patient_township_id = n;
      }
    },
    /** Flatten relation objects; normalize outside flags and ids for edit payloads. */
    syncTownshipIdsFromPayload() {
      const s = this.supervisionData;
      if (!s || typeof s !== "object") return;
      this.syncOutsideTownshipFromPatient();
      const num = (v) => {
        if (v == null || v === "") return null;
        const n = Number(v);
        return Number.isNaN(n) ? null : n;
      };
      const relId = (rel) => {
        if (rel == null || typeof rel !== "object") return null;
        return num(rel.id ?? rel.township_id);
      };

      const osp = s.outside_tsp;
      if (
        (s.outside_township == null || s.outside_township === "") &&
        osp != null &&
        osp !== ""
      ) {
        const n = Number(osp);
        if (n === 2 || osp === "2" || osp === true)
          this.$set(this.supervisionData, "outside_township", "2");
        else if (n === 1 || osp === "1")
          this.$set(this.supervisionData, "outside_township", "1");
      }

      let pt =
        relId(s.patient_township) ??
        relId(s.township) ??
        relId(s.patient?.township);
      if (pt == null && s.township_id != null && s.patient_township_id == null) {
        pt = num(s.township_id);
      }
      if (pt != null) s.patient_township_id = pt;
      else if (s.patient_township_id != null)
        s.patient_township_id = num(s.patient_township_id);

      const vt =
        relId(s.volunteer_township) ?? relId(s.supporting_township);
      if (vt != null) s.volunteer_township_id = vt;
      else if (s.volunteer_township_id != null)
        s.volunteer_township_id = num(s.volunteer_township_id);

      let oid = s.outside_township_id;
      if (oid != null && typeof oid === "object") oid = oid.id;
      if (
        (oid == null || oid === "") &&
        s.outside_tsp_name != null &&
        s.outside_tsp_name !== ""
      ) {
        oid = s.outside_tsp_name;
      }
      s.outside_township_id = oid != null && oid !== "" ? num(oid) : null;

      let ot = s.outside_township;
      if (ot != null && typeof ot === "object") {
        const nestedId = relId(ot);
        if (nestedId != null) {
          s.outside_township_id = nestedId;
          ot = "2";
        } else {
          ot = s.outside_township_id != null ? "2" : "1";
        }
      } else if (ot == null || ot === "") {
        ot = s.outside_township_id != null ? "2" : "1";
      } else {
        const raw = String(ot).trim();
        const asNum = Number(raw);
        if (raw === "2" || asNum === 2) ot = "2";
        else if (raw === "1" || asNum === 1) ot = "1";
        else if (!Number.isNaN(asNum) && asNum === 2) ot = "2";
        else if (!Number.isNaN(asNum) && asNum === 1) ot = "1";
        else ot = s.outside_township_id != null ? "2" : "1";
      }
      this.$set(this.supervisionData, "outside_township", ot);

      if (s.mdrtb_monopoly == null && s.monopoly != null) {
        s.mdrtb_monopoly = num(s.monopoly);
      }
      if (s.dots_type != null && typeof s.dots_type === "string") {
        s.dots_type = num(s.dots_type);
      }
      if (
        s.supervision_type != null &&
        typeof s.supervision_type === "string"
      ) {
        s.supervision_type = num(s.supervision_type);
      }
    },
    coerceOutsideTownshipRowId(raw) {
      if (raw == null || raw === "") return null;
      if (typeof raw === "object" && raw.id != null) {
        const n = Number(raw.id);
        return Number.isNaN(n) ? null : n;
      }
      const n = Number(raw);
      return Number.isNaN(n) ? null : n;
    },
    matchOutsideTownshipSelectValue(targetId) {
      if (targetId == null || targetId === "") return null;
      const list = this.outside_townships || [];
      const n = Number(targetId);
      const row = list.find((r) => {
        const rid = r.id ?? r.outside_tsp;
        return (
          rid === targetId ||
          Number(rid) === n ||
          String(rid) === String(targetId)
        );
      });
      if (!row) return this.coerceOutsideTownshipRowId(targetId);
      return row.id != null ? row.id : row.outside_tsp;
    },
    applyOutsideTownshipSelectionAfterListLoads() {
      if (!this.showOutsideTownshipSelect) return;
      this.normalizeOutsideTownshipFromPayload();
      const s = this.supervisionData;
      const raw =
        s.outside_township_id ??
        s.outside_tsp_name ??
        (s.patient && s.patient.outside_tsp_name);
      const numId = this.coerceOutsideTownshipRowId(raw);
      if (numId == null) return;
      const matched = this.matchOutsideTownshipSelectValue(numId);
      if (matched != null)
        this.$set(this.supervisionData, "outside_township_id", matched);
    },
    async fetchOutsideTownshipsIfNeeded({ clearOutsideId = false } = {}) {
      if (!this.showOutsideTownshipSelect) return;
      const tid = this.supervisionData.patient_township_id;
      if (!tid) return;
      if (clearOutsideId) this.supervisionData.outside_township_id = null;
      try {
        await this.getOutsideTownships(tid);
      } finally {
        await this.$nextTick();
        this.applyOutsideTownshipSelectionAfterListLoads();
      }
    },
    changeVolunteer() {
      if (this.supervisionData.volunteer_id) {
        this.$store.dispatch("Patient/getPatientsByVolunteer", {
          volunteer_id: this.supervisionData.volunteer_id,
        });
      }
    },
    handleSupportingTownship(id) {
      this.$store.dispatch("Volunteer/getVolunteerByTwnId", id);
    },
    showText(id) {
      let resultText = this.radioText.find((e) => e.id == id);
      if (resultText) return resultText.text;
    },
    emitData() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        let data = {
          volunteer_id: this.supervisionData.volunteer_id,
          dots_type: this.supervisionData.dots_type,
          supervision_type: this.supervisionData.supervision_type,
          date: this.supervisionData.date,
          radio_value_list: this.supervisionData.radio_value_list,
          remark: this.supervisionData.remark,
          month: this.$route.params.month,
          year: this.$route.params.year,
          mdrtb_code: this.supervisionData.mdrtb_code,
          mdrtb_year: this.supervisionData.mdrtb_year,
          mdrtb_monopoly:this.supervisionData.mdrtb_monopoly,
          township_id:this.admin.township_id,
          patient_township_id:this.supervisionData.patient_township_id,
          volunteer_township_id:this.supervisionData.volunteer_township_id,
          outside_township: this.supervisionData.outside_township,
          ...(this.showOutsideTownshipSelect &&
            this.supervisionData.outside_township_id != null && {
              outside_township_id: this.supervisionData.outside_township_id,
            }),
        };

       // const threeDigitPattern = /^[0-9]{3}$/;
        const twoDigitPattern = /^.*[1-9]{2}$/;
        if (
          twoDigitPattern.test(this.supervisionData.mdrtb_year)
        ) {
          this.$emit("emitData", data);
        } else {
          // if (!threeDigitPattern.test(this.supervisionData.mdrtb_code))
          //   this.$toast.error(
          //     "code must has at least three digit."
          //   );
          
          if (!twoDigitPattern.test(this.supervisionData.mdrtb_year))
            this.$toast.error("year must has at least two digit.");
          this.clearToastErrors();
        }
      }
    },
  },
  created() {
    this.$store.dispatch("Volunteer/getVolunteers");
    
    //  this.$store.dispatch(
    //   "Volunteer/getVolunteerByTwnId",
    //   !this.supervisionData.patient_township_id? Number( this.admin.township_id):this.supervisionData.patient_township_id,
    // );
    this.getAllTownships();
    const otInit = this.supervisionData.outside_township;
    const normalizedOtInit =
      otInit != null && otInit !== ""
        ? String(otInit).trim()
        : "1";
    this.$set(this.supervisionData, "outside_township", normalizedOtInit);
    this.syncTownshipIdsFromPayload();
    this.normalizeOutsideTownshipFromPayload();
    if (
      this.supervisionData.patient_township_id == null ||
      this.supervisionData.patient_township_id === ""
    ) {
      this.supervisionData.patient_township_id = Number(this.admin.township_id);
    }
    if (
      this.supervisionData.volunteer_township_id == null ||
      this.supervisionData.volunteer_township_id === ""
    ) {
      this.supervisionData.volunteer_township_id = Number(
        this.admin.township_id
      );
    }
    this.supervisionData.township_id = Number(this.admin.township_id);
    this.fetchOutsideTownshipsIfNeeded({ clearOutsideId: false });
    if (this.supervisionData.volunteer_township_id != null) {
      this.handleSupportingTownship(this.supervisionData.volunteer_township_id);
    }
    if (this.supervisionData.volunteer_id) {
      this.$nextTick(() => this.changeVolunteer());
    }
  },
  mounted() {
    this.fetchOutsideTownshipsIfNeeded({ clearOutsideId: false });
    if (this.supervisionData.volunteer_township_id != null) {
      this.handleSupportingTownship(this.supervisionData.volunteer_township_id);
    }
  },
  beforeMount() {
    this.supervisionData.date = !this.supervisionData.date ? this.minDate
      : this.supervisionData.date;
  },
};
</script>

<style scoped>
.outside-township-card {
  border-radius: 8px;
  border-color: rgba(0, 0, 0, 0.12);
  background-color: rgba(0, 150, 136, 0.04);
}

.outside-township-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
}

.text-muted {
  color: #837e7e;
}

.outside-township-radio {
  flex-shrink: 0;
}
</style>
